import React, { useEffect } from 'react';
import { useSearchParams, useLocation, useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAnalytics } from '../context/AnalyticsContext';
import { useGetBooking, BookingDetails } from '../api';
import { CampFormatUtils } from '../utils/campFormatUtils';
import { CheckCircle, Calendar, Users, Clock } from 'lucide-react';
import './Confirmation.css';

const Confirmation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { trackPageView, trackEvent } = useAnalytics();

  const bookingUuid = searchParams.get('booking_uuid') || '';
  const initialBooking = (location.state as { booking?: BookingDetails } | null)?.booking;

  const { data: fetchedBooking, isLoading, error } = useGetBooking(bookingUuid, {
    enabled: !initialBooking && Boolean(bookingUuid),
  });

  const booking = initialBooking || fetchedBooking;

  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView('/confirmation', 'Booking Confirmation');
  }, [trackPageView]);

  useEffect(() => {
    if (!bookingUuid) {
      navigate('/search');
    }
  }, [bookingUuid, navigate]);

  useEffect(() => {
    if (error) {
      navigate('/search');
    }
  }, [error, navigate]);

  useEffect(() => {
    if (booking) {
      trackEvent('confirmation_page_viewed', {
        booking_uuid: booking.uuid,
        camp_id: booking.camp.id,
      });
    }
  }, [booking, trackEvent]);

  if (isLoading || !booking) {
    return (
      <div className="confirmation-page">
        <Header />
        <main className="confirmation-main">
          <div className="confirmation-container">
            <div className="confirmation-loading">Loading...</div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const camp = booking.camp;

  return (
    <div className="confirmation-page">
      <Header />

      <main className="confirmation-main">
        <div className="confirmation-container">
          {/* Success Header */}
          <div className="confirmation-header">
            <div className="confirmation-icon">
              <CheckCircle className="icon icon--xl" />
            </div>
            <h1 className="confirmation-title">Booking Confirmed!</h1>
            <p className="confirmation-subtitle">
              Your camp booking has been successfully completed.
            </p>
          </div>

          {/* Booking Details Card */}
          <div className="confirmation-card">
            <div className="confirmation-camp-header">
              <img
                src={camp.images?.[0]?.url || camp.photo_url || '/placeholder-camp.jpg'}
                alt={camp.name}
                className="confirmation-camp-image"
              />
              <div className="confirmation-camp-info">
                <h2 className="confirmation-camp-name">{camp.name}</h2>
                <div className="confirmation-camp-details">
                  <div className="confirmation-detail-item">
                    <Clock className="icon icon--sm" />
                    <span>{CampFormatUtils.formatTimeRange(camp.start_time, camp.end_time)}</span>
                  </div>
                  <div className="confirmation-detail-item">
                    <Users className="icon icon--sm" />
                    <span>Ages: {CampFormatUtils.formatAgeRange(camp.min_age, camp.max_age)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Selected Dates */}
            <div className="confirmation-section">
              <h3 className="confirmation-section-title">
                <Calendar className="icon icon--sm" />
                Selected Dates
              </h3>
              <ul className="confirmation-dates-list">
                {booking.camp_dates.map((date) => (
                  <li key={date.id} className="confirmation-date-item">
                    {CampFormatUtils.formatDateRange(date.start_date, date.end_date)}
                  </li>
                ))}
              </ul>
            </div>

            {/* Participants */}
            {booking.participants && booking.participants.length > 0 && (
              <div className="confirmation-section">
                <h3 className="confirmation-section-title">
                  <Users className="icon icon--sm" />
                  Participants
                </h3>
                <ul className="confirmation-participants-list">
                  {booking.participants.map((participant, index) => (
                    <li key={index} className="confirmation-participant-item">
                      <span className="confirmation-participant-name">{participant.name}</span>
                      {participant.birthdate && (
                        <span className="confirmation-participant-birthdate">
                          Born: {new Date(participant.birthdate).toLocaleDateString()}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Contact Info */}
            {booking.contact && (
              <div className="confirmation-section">
                <h3 className="confirmation-section-title">Contact Information</h3>
                <div className="confirmation-contact">
                  <p><strong>Name:</strong> {booking.contact.name}</p>
                  <p><strong>Email:</strong> {booking.contact.email}</p>
                  {booking.contact.phone && (
                    <p><strong>Phone:</strong> {booking.contact.phone}</p>
                  )}
                  {booking.contact.relationship_to_participant && (
                    <p><strong>Relationship:</strong> {booking.contact.relationship_to_participant}</p>
                  )}
                </div>
              </div>
            )}

            {/* Total */}
            <div className="confirmation-total">
              <span className="confirmation-total-label">Total Paid</span>
              <div className="confirmation-total-prices">
                {booking.original_price !== undefined && booking.original_price !== booking.total_price && (
                  <span className="confirmation-original-price">${booking.original_price}</span>
                )}
                <span className="confirmation-total-amount">${booking.total_price}</span>
              </div>
              {booking.discount_code && (
                <span className="confirmation-discount-note">
                  {booking.discount_code} applied
                </span>
              )}
            </div>
          </div>

          {/* Email Notice */}
          {booking.contact?.email && (
            <p className="confirmation-email-notice">
              A confirmation email has been sent to {booking.contact.email}.
            </p>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Confirmation;
